'use client';

/**
 * Footer Component
 * ================
 *
 * Site-wide footer with logo, quick links, legal links, and social media.
 * Matches the liquid glass theme of the site.
 */

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';

// The footer renders on every page, but the legal modal and its four policy
// texts are only needed after a click. Loading them on demand keeps them out
// of every page's initial JavaScript.
const LegalModal = dynamic(() => import('@/components/ui/LegalModal'));
const ReraDisclaimer = dynamic(() => import('@/components/legal/ReraDisclaimer'));
const PrivacyPolicy = dynamic(() => import('@/components/legal/PrivacyPolicy'));
const CookiePolicy = dynamic(() => import('@/components/legal/CookiePolicy'));
const TermsConditions = dynamic(() => import('@/components/legal/TermsConditions'));

type LegalModalType = 'rera' | 'privacy' | 'cookie' | 'terms' | null;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState<LegalModalType>(null);

  // Quick links matching Regalia project pages
  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Amenities', href: '/amenities' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Layouts', href: '/layouts' },
  ];

  // Legal links with modal types
  const legalLinks: { name: string; modal: LegalModalType }[] = [
    { name: 'RERA Disclaimer', modal: 'rera' },
    { name: 'Privacy Policy', modal: 'privacy' },
    { name: 'Cookie Policy', modal: 'cookie' },
    { name: 'Terms & Conditions', modal: 'terms' },
  ];

  // Social media links
  const socialLinks = [
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/magnategroupco',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/magnategroupco',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  // Get modal title based on type
  const getModalTitle = (type: LegalModalType): string => {
    switch (type) {
      case 'rera': return 'RERA Disclaimer';
      case 'privacy': return 'Privacy Policy';
      case 'cookie': return 'Cookie Policy';
      case 'terms': return 'Terms & Conditions';
      default: return '';
    }
  };

  // Get modal content based on type
  const getModalContent = (type: LegalModalType): React.ReactNode => {
    switch (type) {
      case 'rera': return <ReraDisclaimer />;
      case 'privacy': return <PrivacyPolicy />;
      case 'cookie': return <CookiePolicy />;
      case 'terms': return <TermsConditions />;
      default: return null;
    }
  };

  return (
    <>
      <footer className="relative pt-20 pb-8 bg-white/5 backdrop-blur-sm border-t border-white/10">
        {/* Top gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer Top */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8">
            {/* Logo Section */}
            <div className="flex flex-col gap-8 lg:pr-8 lg:border-r lg:border-white/10">
              {/* Logo and Tagline */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative w-24 h-24">
                  <Image
                    src="/assets/LOGOS/magnate_logo.svg"
                    alt="Magnate Group"
                    fill
                    className="object-contain brightness-0 invert"
                  />
                </div>
                <p className="text-white/90 font-semibold uppercase tracking-wider text-sm">
                  Building Excellence
                </p>
              </div>

              {/* Social Section */}
              <div className="flex flex-col items-center gap-4 mt-4">
                <h2 className="text-white/90 font-semibold text-base">Follow Us</h2>
                <div className="flex gap-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      className="flex items-center justify-center w-10 h-10 bg-white/5 border border-white/10 rounded-full text-white/70 hover:bg-white/10 hover:text-white hover:-translate-y-1 transition-all duration-300"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Links Section */}
            <div className="flex flex-col gap-8 lg:pl-8">
              {/* Quick Links */}
              <div>
                <h2 className="text-white/90 font-semibold text-base mb-6 relative pb-3">
                  Quick Links
                  <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-white/30" />
                </h2>
                <ul className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {quickLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-white/60 hover:text-white hover:pl-1 transition-all duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h2 className="text-white/90 font-semibold text-base mb-6 relative pb-3">
                  Legal
                  <span className="absolute bottom-0 left-0 w-10 h-0.5 bg-white/30" />
                </h2>
                <ul className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {legalLinks.map((link) => (
                    <li key={link.name}>
                      <button
                        type="button"
                        onClick={() => setActiveModal(link.modal)}
                        className="text-white/60 hover:text-white hover:pl-1 transition-all duration-300 text-sm bg-transparent border-none cursor-pointer text-left"
                      >
                        {link.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-white/10 pt-8 text-center">
            <div className="flex flex-col items-center gap-2">
              <p className="text-white/50 text-sm">
                &copy; {currentYear} Magnate Group. All Rights Reserved.
              </p>
              <p className="text-white/60 text-xs">
                Developed by{' '}
                <a
                  href="mailto:ashutoshdp49@gmail.com"
                  className="text-white/50 hover:text-white/80 hover:underline transition-colors"
                >
                  Ashutosh Patel
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modals - only mounted (and downloaded) once one is opened */}
      {activeModal !== null && (
        <LegalModal
          isOpen
          onClose={() => setActiveModal(null)}
          title={getModalTitle(activeModal)}
        >
          {getModalContent(activeModal)}
        </LegalModal>
      )}
    </>
  );
};

export default Footer;
